/**
 * Hashing and serialising the trade layer.
 *
 * Kept in its own file so `campaign/hash.ts` and `campaign/save.ts` each gain one
 * line rather than sixty, but the rule those two files state applies here word
 * for word: **the field list below is exactly the field list `hashTrade` walks.**
 * If they drift, a campaign loads into a board that looks right and then quietly
 * stops being the board that was saved.
 *
 * Everything in `TradeState` goes in, including the node graph. The graph is
 * built from the province table at the start of a campaign and never mutated
 * afterwards — but it is built from a *generated* realm, and a realm that grows
 * or is reseeded produces a different graph. A campaign that hashed only the
 * mutable half would call two boards identical up to the moment their arrows
 * pointed different ways. Same argument as `CampaignState.links`, same answer.
 *
 * `control` and `income` are the interesting entries. Control is smoothed month
 * over month, so it is genuinely path-dependent state — two boards that look
 * identical can be paying different crowns because one of them took its market
 * last season. Income is last month's payout, which is redundant *only* if you
 * never look at it; the panel does, the council does, and a hash that ignored it
 * would let two campaigns disagree about what everyone had just been paid.
 */

import type { Hasher } from '../../core/hash.ts';
import {
  createTradeNodeState,
  MerchantMission,
  type Merchant,
  type TradeNode,
  type TradeNodeState,
  type TradeState,
} from './tradetypes.ts';

export function hashTrade(h: Hasher, t: TradeState): void {
  h.u32(t.nextMerchantId);

  h.u32(t.nodes.length);
  for (const n of t.nodes) {
    h.u32(n.id);
    h.str(n.name);
    h.i32(n.seat);
    h.u32(n.depth);
    h.u32(n.provinces.length);
    for (const p of n.provinces) h.i32(p);
    h.u32(n.outgoing.length);
    for (const o of n.outgoing) h.i32(o);
    h.u32(n.outWeight.length);
    for (const w of n.outWeight) h.num(w);
  }

  h.u32(t.states.length);
  for (const s of t.states) {
    h.num(s.local);
    h.num(s.value);
    h.num(s.retained);
    h.num(s.collectShare);
    h.num(s.steerShare);
    h.u32(s.control.length);
    for (const v of s.control) h.num(v);
    h.u32(s.income.length);
    for (const v of s.income) h.num(v);
    h.u32(s.outflow.length);
    for (const v of s.outflow) h.num(v);
  }

  h.u32(t.merchants.length);
  for (const m of t.merchants) {
    h.u32(m.id);
    h.i32(m.nation);
    h.i32(m.node);
    h.u32(m.mission);
    h.i32(m.toward);
    h.u32(m.travel);
  }
}

export type TradeSave = TradeState;

export function saveTrade(t: TradeState): TradeSave {
  return {
    nodes: t.nodes.map((n) => ({
      ...n,
      provinces: n.provinces.slice(),
      outgoing: n.outgoing.slice(),
      outWeight: n.outWeight.slice(),
    })),
    states: t.states.map((s) => ({
      ...s,
      control: s.control.slice(),
      income: s.income.slice(),
      outflow: s.outflow.slice(),
    })),
    merchants: t.merchants.map((m) => ({ ...m })),
    nextMerchantId: t.nextMerchantId,
  };
}

/**
 * Read one back, against a freshly grown graph.
 *
 * Three cases, and the middle one is the reason this takes `fresh` at all.
 *
 * - **No trade in the save.** A campaign from before this existed. It gets the
 *   fresh graph with nobody's merchants in it and nobody's control — which is
 *   wrong for about four months of play and then indistinguishable, because
 *   control drifts toward what the board says. `SAVE_VERSION` is untouched, per
 *   the rule in `COORDINATION.md`; every line here is a `??` default and every
 *   one of them is idempotent.
 * - **A graph that does not match this build's.** A realm that has been regrown
 *   or resized. The saved node lists would name provinces that mean something
 *   else now, so the graph is rebuilt and the merchants are kept only where they
 *   still point at a node that exists. Better a merchant in roughly the right
 *   market than a node list describing a realm nobody has.
 * - **A matching graph.** Everything comes back verbatim, and the campaign
 *   round-trips exactly. That is what `tests/trade.test.ts` asserts.
 */
export function loadTrade(
  raw: TradeSave | null | undefined,
  fresh: TradeState,
  nations: number,
): TradeState {
  if (!raw || !Array.isArray(raw.nodes) || raw.nodes.length === 0) {
    return {
      nodes: fresh.nodes,
      states: fresh.states,
      merchants: [],
      nextMerchantId: 1,
    };
  }

  const matches =
    raw.nodes.length === fresh.nodes.length &&
    raw.nodes.every((n, i) => n?.seat === fresh.nodes[i].seat);

  const nodes: TradeNode[] = matches
    ? raw.nodes.map((n, i) => ({
        id: n.id ?? i,
        name: n.name ?? fresh.nodes[i].name,
        seat: n.seat ?? fresh.nodes[i].seat,
        provinces: (n.provinces ?? fresh.nodes[i].provinces).slice(),
        depth: n.depth ?? fresh.nodes[i].depth,
        outgoing: (n.outgoing ?? fresh.nodes[i].outgoing).slice(),
        outWeight: (n.outWeight ?? fresh.nodes[i].outWeight).slice(),
      }))
    : fresh.nodes;

  const states: TradeNodeState[] = nodes.map((_, i) => {
    const s = matches ? raw.states?.[i] : undefined;
    const base = createTradeNodeState(nations);
    if (!s) return base;
    return {
      local: s.local ?? 0,
      value: s.value ?? 0,
      retained: s.retained ?? 0,
      collectShare: s.collectShare ?? 0,
      steerShare: s.steerShare ?? 0,
      control: sized(s.control, nations),
      income: sized(s.income, nations),
      outflow: (s.outflow ?? []).slice(0, nodes[i].outgoing.length),
    };
  });

  const merchants: Merchant[] = (raw.merchants ?? [])
    .filter((m) => m && m.node >= 0 && m.node < nodes.length && m.nation >= 0 && m.nation < nations)
    .map((m) => ({
      id: m.id,
      nation: m.nation,
      node: m.node,
      mission: m.mission === MerchantMission.Steer ? MerchantMission.Steer : MerchantMission.Collect,
      // A steer whose road no longer exists becomes a collect where it stands,
      // rather than a merchant pushing carts at a market nothing runs to.
      toward: nodes[m.node].outgoing.includes(m.toward ?? -1) ? m.toward : -1,
      travel: Math.max(0, m.travel ?? 0),
    }))
    .map((m) => (m.mission === MerchantMission.Steer && m.toward < 0
      ? { ...m, mission: MerchantMission.Collect }
      : m))
    .sort((a, b) => a.nation - b.nation || a.node - b.node || a.id - b.id);

  let nextId = raw.nextMerchantId ?? 1;
  for (const m of merchants) if (m.id >= nextId) nextId = m.id + 1;

  return { nodes, states, merchants, nextMerchantId: nextId };
}

function sized(src: number[] | undefined, n: number): number[] {
  const out = new Array<number>(n).fill(0);
  if (!src) return out;
  for (let i = 0; i < n; i++) {
    const v = src[i] ?? 0;
    out[i] = v === 0 ? 0 : v;
  }
  return out;
}
